<template>
  <div class="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-4">
    <div class="flex items-center gap-2">
      <FontAwesomeIcon :icon="['fas', 'flag']" class="text-amber-600" />
      <h3 class="text-sm font-semibold text-amber-800">Needs Triage</h3>
    </div>

    <!-- Assign Tenant -->
    <div>
      <label class="block text-xs font-medium text-gray-600 mb-1">Assign Tenant</label>
      <div class="relative">
        <input
          v-model="tenantSearch"
          type="text"
          class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          :placeholder="selectedTenantName || 'Search tenants...'"
          @click.stop="showTenantDropdown = true"
          @focus="showTenantDropdown = true"
          @input="debouncedSearchTenants"
        />
        <FontAwesomeIcon
          v-if="tenantsLoading"
          :icon="['fas', 'spinner']"
          class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin text-xs"
        />
        <div
          v-if="showTenantDropdown && tenantResults.length"
          class="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
          @click.stop
        >
          <button
            v-for="t in tenantResults"
            :key="t.id"
            class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
            :class="t.id === selectedTenantId ? 'bg-primary-50 text-primary-700' : 'text-gray-700'"
            @click="selectTenant(t)"
          >
            {{ t.name }}
          </button>
        </div>
      </div>
    </div>

    <!-- Assign Requester (only after tenant is selected) -->
    <div v-if="selectedTenantId">
      <label class="block text-xs font-medium text-gray-600 mb-1">Assign Requester</label>
      <div class="relative">
        <input
          v-model="requesterSearch"
          type="text"
          class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
          :placeholder="selectedRequesterName || 'Search users...'"
          @click.stop="showRequesterDropdown = true"
          @focus="showRequesterDropdown = true"
          @input="debouncedSearchRequesters"
        />
        <FontAwesomeIcon
          v-if="requestersLoading"
          :icon="['fas', 'spinner']"
          class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin text-xs"
        />
        <div
          v-if="showRequesterDropdown && requesterResults.length"
          class="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
          @click.stop
        >
          <button
            v-for="u in requesterResults"
            :key="u.id"
            class="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
            :class="u.id === selectedRequesterId ? 'bg-primary-50 text-primary-700' : 'text-gray-700'"
            @click="selectRequester(u)"
          >
            {{ u.full_name || u.email }}
          </button>
        </div>
      </div>
    </div>

    <!-- Sender email info -->
    <div v-if="senderEmail" class="text-xs text-gray-500">
      <FontAwesomeIcon :icon="['fas', 'envelope']" class="mr-1" />
      Original sender: <span class="font-medium text-gray-700">{{ senderEmail }}</span>
    </div>

    <!-- Assign button -->
    <button
      class="w-full text-sm font-medium px-4 py-2 rounded transition-colors disabled:opacity-50"
      :class="canAssign ? 'bg-primary-500 text-white hover:bg-primary-600' : 'bg-gray-200 text-gray-500'"
      :disabled="!canAssign || assigning"
      @click="assign"
    >
      <FontAwesomeIcon v-if="assigning" :icon="['fas', 'spinner']" class="animate-spin mr-1" />
      {{ assigning ? 'Assigning...' : 'Assign Ticket' }}
    </button>
  </div>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash/debounce';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useToast } from 'vue-toastification';

export default {
  name: 'TriagePanel',
  components: { FontAwesomeIcon },
  props: {
    requirementId: { type: [Number, String], required: true },
    senderEmail: { type: String, default: null },
  },
  emits: ['triaged'],
  data() {
    return {
      tenantSearch: '',
      tenantResults: [],
      tenantsLoading: false,
      showTenantDropdown: false,
      selectedTenantId: null,
      selectedTenantName: null,
      selectedTenantSlug: null,
      requesterSearch: '',
      requesterResults: [],
      requestersLoading: false,
      showRequesterDropdown: false,
      selectedRequesterId: null,
      selectedRequesterName: null,
      assigning: false,
    };
  },
  computed: {
    toast() {
      return useToast();
    },
    canAssign() {
      return this.selectedTenantId && this.selectedRequesterId;
    },
  },
  created() {
    this.debouncedSearchTenants = debounce(this.searchTenants, 300);
    this.debouncedSearchRequesters = debounce(this.searchRequesters, 300);
  },
  mounted() {
    this.searchTenants();
    document.addEventListener('click', this.onDocClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
  },
  methods: {
    onDocClick() {
      this.showTenantDropdown = false;
      this.showRequesterDropdown = false;
    },
    async searchTenants() {
      this.tenantsLoading = true;
      try {
        const { data } = await axios.get('/api/tenants/', {
          params: { search: this.tenantSearch || undefined },
        });
        this.tenantResults = Array.isArray(data) ? data : data.results || [];
      } catch {
        this.tenantResults = [];
      } finally {
        this.tenantsLoading = false;
      }
    },
    selectTenant(tenant) {
      this.selectedTenantId = tenant.id;
      this.selectedTenantName = tenant.name;
      this.selectedTenantSlug = tenant.slug || tenant.name?.toLowerCase().replace(/\s+/g, '-');
      this.tenantSearch = '';
      this.showTenantDropdown = false;
      this.selectedRequesterId = null;
      this.selectedRequesterName = null;
      this.requesterResults = [];
      this.searchRequesters();
    },
    async searchRequesters() {
      if (!this.selectedTenantId) return;
      this.requestersLoading = true;
      try {
        const { data } = await axios.get('/api/admin/user/', {
          params: {
            search: this.requesterSearch || undefined,
            tenant_slug: this.selectedTenantSlug,
          },
        });
        this.requesterResults = Array.isArray(data) ? data : data.results || [];
      } catch {
        this.requesterResults = [];
      } finally {
        this.requestersLoading = false;
      }
    },
    selectRequester(user) {
      this.selectedRequesterId = user.id;
      this.selectedRequesterName = user.full_name || user.email;
      this.requesterSearch = '';
      this.showRequesterDropdown = false;
    },
    async assign() {
      if (!this.canAssign) return;
      this.assigning = true;
      try {
        await axios.patch(`/api/requirements/${this.requirementId}/`, {
          tenant: this.selectedTenantId,
          requester: this.selectedRequesterId,
        });
        this.toast.success(`Ticket assigned to ${this.selectedTenantName}`);
        this.$emit('triaged', {
          tenantId: this.selectedTenantId,
          tenantName: this.selectedTenantName,
          requesterId: this.selectedRequesterId,
          requesterName: this.selectedRequesterName,
        });
      } catch (err) {
        const detail = err.response?.data?.detail || 'Failed to assign ticket.';
        this.toast.error(detail);
      } finally {
        this.assigning = false;
      }
    },
  },
};
</script>
